// src/components/TagsProvider.tsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { TagRegistry } from '../types/position.ts';
import { buildRegistry } from '../utils/tags.ts';
import { fetchTagCategories, fetchTags } from '../utils/tagsApi.ts';

interface TagsContextValue {
  registry: TagRegistry;
  loading: boolean;
  error: string | null;
  ready: boolean;
  refresh: () => Promise<void>;
}

const emptyRegistry: TagRegistry = buildRegistry([], [], 0);

const TagsContext = createContext<TagsContextValue>({
  registry: emptyRegistry,
  loading: false,
  error: null,
  ready: false,
  refresh: async () => {},
});

export function TagsProvider({
  scopeUserId,
  children,
}: {
  scopeUserId: number | null;
  children: React.ReactNode;
}) {
  const [registry, setRegistry] = useState<TagRegistry>(emptyRegistry);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const versionRef = useRef(0);

  const refresh = useCallback(async () => {
    if (scopeUserId == null) return;
    setLoading(true);
    setError(null);
    try {
      const [cats, tags] = await Promise.all([
        fetchTagCategories(scopeUserId),
        fetchTags(null, scopeUserId),
      ]);
      versionRef.current += 1;
      setRegistry(buildRegistry(cats, tags, versionRef.current));
      setReady(true);
    } catch (err) {
      console.error('tags registry load failed', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [scopeUserId]);

  useEffect(() => {
    setReady(false);
    refresh();
  }, [refresh]);

  const value = useMemo<TagsContextValue>(
    () => ({ registry, loading, error, ready, refresh }),
    [registry, loading, error, ready, refresh],
  );
  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
}

export function useTagsRegistry(): TagsContextValue {
  return useContext(TagsContext);
}
